// 定义本项目所需要用到的接口请求类
import NetworkService, {ApiResponse} from "./network-service.ts";
import {
    AI_CHALLENGE_WORD_LIST,
    AVATAR_FRAME_EQUIPPED,
    AVATAR_FRAME_LIST,
    CHECK_VS_PLAYER_INVITED_ENTERED,
    COUNTRY_LIST,
    CREATE_UGC_WORD,
    CREATE_VS_PLAYER_ROOM,
    DAILY_GAME_CORRECT_WORD,
    DAILY_GAME_NEXT_TIME,
    DAILY_GAME_PLAY_OVER,
    DAILY_GAME_PLAY_STATUS, GAME_WIN_GET_REWARD, GET_USER_INFO_BY_ID,
    JOIN_VS_PLAYER_ROOM,
    LOAD_AI_GAME_RANK,
    LOAD_DAILY_GAME_RANK,
    LOAD_GAME_DATA_PROFILE,
    LOAD_UGC_WORD_LIST,
    LOAD_UGC_WORD_TO_PLAY,
    LOAD_USER_EQUIPPED_LIST,
    LOAD_VS_PLAYER_ROOM_LIST,
    LOGIN_BY_CGTN,
    LOGIN_BY_GUEST, LOGIN_GET_REWARD, LOGOUT,
    MEDAL_LIST,
    SET_AVATAR_FRAME_EQUIP_STATUS,
    SET_MEDAL_EQUIP_STATUS,
    SET_THEME_EQUIP_STATUS, SET_UGC_WORD_LIKE, SHARE_GET_REWARD,
    SUBMIT_AI_CHALLENGE_RESULT, SUBMIT_UGC_RESULT,
    SUBMIT_VS_PLAYER_RESULT,
    THEME_EQUIPPED_SETTINGS,
    THEME_LIST, UPDATE_AVATAR,
    UPDATE_USER_COUNTRY
} from "./apis.ts";
import {
    IDailyWordStatus,
    IAvatarFrame,
    ICountryRegion,
    IDailyGameCorrectWord,
    IMedal,
    IUserGame,
    ITheme,
    IThemeSetting,
    IUser,
    IAIChallengeWord,
    IDailyGameRankingData,
    IAIChallengeRankData,
    IVSPlayerRoomInfo,
    IVSPlayerGameResult,
    IUGCWord, IUgcGameResponse, IUserInfo, IAwardResult
} from "./IResponse.ts";
import {IAIGameResult, ICreateUgc, ILoginByCGTN, IUgcGameResult} from "./IParams.ts";
import {AESUtils} from "../AESUtils.ts";


class APIService {

    private networkService = new NetworkService();

    // 退出登录
    public async logout(): Promise<ApiResponse<string>> {
        return this.networkService.get<string>(LOGOUT);
    }

    // 游客登录
    public async loginByGuest(): Promise<ApiResponse<IUser | undefined>> {
        return this.networkService.get<IUser | undefined>(LOGIN_BY_GUEST);
    }

    // CGTN登录
    public async loginByCGTN(data: ILoginByCGTN): Promise<ApiResponse<IUser | undefined>> {
        // 将实体类转换为字符串
        const dataStr = JSON.stringify(data)
        // 将数据加密后传递
        const params = AESUtils.encryptData(dataStr)
        return this.networkService.post<IUser | undefined>(LOGIN_BY_CGTN, params);
    }

    // 更新用户头像
    public async updateUserAvatar(avatar: string): Promise<ApiResponse<string>> {
        // 将数据加密后传递
        const params = AESUtils.encryptData(avatar)
        return this.networkService.post<string>(UPDATE_AVATAR, params);
    }

    // profile 获取四个游戏数据
    public async getProfileGameData(): Promise<ApiResponse<IUserGame>> {
        return this.networkService.get<IUserGame>(LOAD_GAME_DATA_PROFILE);
    }

    // 国家/区域选择列表
    public async loadCountryList(): Promise<ApiResponse<ICountryRegion[]>> {
        return this.networkService.get<ICountryRegion[]>(COUNTRY_LIST);
    }

    // 修改用户的国家/区域
    public async updateUserCountry(countryId: number): Promise<ApiResponse<ICountryRegion>> {
        // 将实体类转换为字符串
        const dataStr = JSON.stringify(countryId)
        // 将数据加密后传递
        const params = AESUtils.encryptData(dataStr)
        return this.networkService.post<ICountryRegion>(UPDATE_USER_COUNTRY, params);
    }

    // 获取徽章列表
    public async loadMedalList(): Promise<ApiResponse<IMedal[]>> {
        return this.networkService.get<IMedal[]>(MEDAL_LIST);
    }

    // 装备徽章
    public async equipMedal(medalId: number, equip: boolean): Promise<ApiResponse<string>> {
        return this.networkService.get<string>(SET_MEDAL_EQUIP_STATUS, {medalId, equip});
    }

    // 获取已经装备的徽章列表
    public async loadEquippedMedals(): Promise<ApiResponse<IMedal[]>> {
        return this.networkService.get<IMedal[]>(LOAD_USER_EQUIPPED_LIST);
    }

    // 获取头像框列表
    public async loadAvatarFrameList(): Promise<ApiResponse<IAvatarFrame[]>> {
        return this.networkService.get<IAvatarFrame[]>(AVATAR_FRAME_LIST);
    }

    // 装备头像框
    public async equipAvatarFrame(avatarFrameId: number, equip: boolean): Promise<ApiResponse<string>> {
        return this.networkService.get<string>(SET_AVATAR_FRAME_EQUIP_STATUS, {avatarFrameId, equip});
    }

    // 获取已经装备的头像框
    public async loadEquippedAvatarFrame(): Promise<ApiResponse<IAvatarFrame>> {
        return this.networkService.get<IAvatarFrame>(AVATAR_FRAME_EQUIPPED);
    }

    // 获取主题列表
    public async loadThemeList(): Promise<ApiResponse<ITheme[]>> {
        return this.networkService.get<ITheme[]>(THEME_LIST);
    }

    // 获取主题设置
    public async loadThemeSetting(): Promise<ApiResponse<IThemeSetting>> {
        return this.networkService.get<IThemeSetting>(THEME_EQUIPPED_SETTINGS);
    }

    // 装备主题
    public async equipTheme(themeId: number, equip: boolean): Promise<ApiResponse<string>> {
        return this.networkService.get<string>(SET_THEME_EQUIP_STATUS, {themeId, equip});
    }

    // 获取每日游戏的单词
    public async getDailyGameWords(): Promise<ApiResponse<IDailyGameCorrectWord>> {
        return this.networkService.get<IDailyGameCorrectWord>(DAILY_GAME_CORRECT_WORD);
    }

    // 获取每日游戏的游戏状态，如果为空就是没有
    public async getDailyGamePlayStatus(): Promise<ApiResponse<IDailyWordStatus>> {
        return this.networkService.get<IDailyWordStatus>(DAILY_GAME_PLAY_STATUS);
    }

    // 获取每日游戏的nextTime
    public async getDailyGameNextTime(): Promise<ApiResponse<number>> {
        return this.networkService.get<number>(DAILY_GAME_NEXT_TIME);
    }

    // 每日游戏结束后提交到后端
    public async submitDailyGameResult(result: IDailyWordStatus): Promise<ApiResponse<IDailyWordStatus>> {
        // 将实体类转换为字符串
        const dataStr = JSON.stringify(result)
        const params = AESUtils.encryptData(dataStr)
        return this.networkService.post<IDailyWordStatus>(DAILY_GAME_PLAY_OVER, params);
    }

    // 获取AI对战的单词列表
    public async getAiGameWords(): Promise<ApiResponse<IAIChallengeWord[]>> {
        return this.networkService.get<IAIChallengeWord[]>(AI_CHALLENGE_WORD_LIST);
    }

    // AI对战游戏结束后将游戏结果提交到后端
    public async submitAiGameResult(result: IAIGameResult): Promise<ApiResponse<number>> {
        // 将实体类转换为字符串
        const dataStr = JSON.stringify(result)
        const params = AESUtils.encryptData(dataStr)
        return this.networkService.post<number>(SUBMIT_AI_CHALLENGE_RESULT, params);
    }

    // 获取每日游戏的排行榜
    public async getDailyGameRank(countryCode?: string): Promise<ApiResponse<IDailyGameRankingData>> {
        return this.networkService.get<IDailyGameRankingData>(LOAD_DAILY_GAME_RANK, {countryCode});
    }

    // 获取AI对战游戏的排行榜
    public async getAiGameRank(countryCode?: string): Promise<ApiResponse<IAIChallengeRankData>> {
        return this.networkService.get<IAIChallengeRankData>(LOAD_AI_GAME_RANK, {countryCode});
    }

    // 创建 vs-player 房间
    public async createVsPlayerRoom(hardLevel: number): Promise<ApiResponse<IVSPlayerRoomInfo>> {
        return this.networkService.get<IVSPlayerRoomInfo>(CREATE_VS_PLAYER_ROOM, {hardLevel});
    }

    // 获取 vs-player 被邀请人是否进来房间了
    public async getInvitedPlayerStatus(roomId: string): Promise<ApiResponse<IVSPlayerRoomInfo>> {
        return this.networkService.get<IVSPlayerRoomInfo>(CHECK_VS_PLAYER_INVITED_ENTERED, {roomId});
    }

    // 获取 vs-player 房间列表
    public async getVsPlayerRoomList(): Promise<ApiResponse<IVSPlayerRoomInfo[]>> {
        return this.networkService.get<IVSPlayerRoomInfo[]>(LOAD_VS_PLAYER_ROOM_LIST);
    }

    // 加入 vs-player 房间
    public async joinVsPlayerRoom(roomId: string): Promise<ApiResponse<IVSPlayerRoomInfo>> {
        return this.networkService.get<IVSPlayerRoomInfo>(JOIN_VS_PLAYER_ROOM, {roomId});
    }

    // 提交 vs-player 结果
    public async submitVsPlayerResult(result: IVSPlayerGameResult): Promise<ApiResponse<IVSPlayerRoomInfo>> {
        // 将实体类转换为字符串
        const dataStr = JSON.stringify(result)
        const params = AESUtils.encryptData(dataStr)
        return this.networkService.post<IVSPlayerRoomInfo>(SUBMIT_VS_PLAYER_RESULT, params);
    }

    // 创建UGC单词
    public async createUGCWord(data: ICreateUgc): Promise<ApiResponse<string>> {
        // 将实体类转换为字符串
        const dataStr = JSON.stringify(data)
        const params = AESUtils.encryptData(dataStr)
        return this.networkService.post<string>(CREATE_UGC_WORD, params);
    }

    // 获取UGC单词列表
    public async getUGCWordList(): Promise<ApiResponse<IUGCWord[]>> {
        return this.networkService.get<IUGCWord[]>(LOAD_UGC_WORD_LIST);
    }

    // 随机获取UGC单词，用来玩游戏
    public async getRUGCWordToPlay(): Promise<ApiResponse<IUGCWord>> {
        return this.networkService.get<IUGCWord>(LOAD_UGC_WORD_TO_PLAY);
    }

    // 提交UGC游戏结果
    public async submitUgcGameResult(result: IUgcGameResult): Promise<ApiResponse<IUgcGameResponse>> {
        const dataStr = JSON.stringify(result)
        const params = AESUtils.encryptData(dataStr)
        return this.networkService.post<IUgcGameResponse>(SUBMIT_UGC_RESULT, params);
    }

    // 设置ugc单词点赞
    public async setUgcWordLike(ugcGameLogId: string, wordId: string, likeStatus: boolean): Promise<ApiResponse<string>> {
        const param = {
            ugcGameLogId: ugcGameLogId,
            wordId: wordId,
            likeStatus: likeStatus
        }
        const dataStr = JSON.stringify(param)
        const params = AESUtils.encryptData(dataStr)
        return this.networkService.post<string>(SET_UGC_WORD_LIKE, params);
    }

    // 登录获取登录奖励
    public async getLoginReward(): Promise<ApiResponse<IAwardResult>> {
        return this.networkService.get<IAwardResult>(LOGIN_GET_REWARD);
    }

    // 游戏胜利获取奖励
    public async getGameWinReward(gameType: string): Promise<ApiResponse<IAwardResult>> {
        return this.networkService.get<IAwardResult>(GAME_WIN_GET_REWARD, {gameType});
    }

    // 分享获取奖励
    public async getShareReward(): Promise<ApiResponse<IAwardResult>> {
        return this.networkService.get<IAwardResult>(SHARE_GET_REWARD);
    }

    // 根据用户id获取用户信息
    public async getUserInfoById(userId: string): Promise<ApiResponse<IUserInfo>> {
        return this.networkService.get<IUserInfo>(GET_USER_INFO_BY_ID, {userId});
    }
}

export const apiService = new APIService();
